import type { Ref } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Inline, Text, Flex, xcss } from '@atlaskit/primitives';

import { PAGE_SIZE_OPTIONS } from '../constants';

import { messages } from './messages';

import type { FastshiftTableDensity, FastshiftTablePaginationControls } from '../types';

const paginationSectionStyles = xcss({
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	borderStartStartRadius: '0',
	borderStartEndRadius: '0',
	borderBlockStart: '0',
	borderStyle: 'solid',
	padding: 'space.150',
});

const compactPaginationSectionStyles = xcss({
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	borderStartStartRadius: '0',
	borderStartEndRadius: '0',
	borderBlockStart: '0',
	borderStyle: 'solid',
	padding: 'space.075',
});

const pageSizeMenuWrapperStyles = xcss({
	width: '80px',
});

export const FastshiftTablePagination = ({
	pagination,
	textSize = 'medium',
	density = 'default',
}: {
	pagination: FastshiftTablePaginationControls;
	textSize?: 'small' | 'medium';
	density?: FastshiftTableDensity;
}) => {
	return (
		<Flex
			justifyContent="space-between"
			alignItems="center"
			xcss={density === 'compact' ? compactPaginationSectionStyles : paginationSectionStyles}
		>
			<Inline alignBlock="center" alignInline="center" space="space.100">
				<Text size={textSize} color="color.text.subtle">
					<FormattedMessage {...messages.resultsPerPageLabel} />
				</Text>
				<Box xcss={pageSizeMenuWrapperStyles}>
					<DropdownMenu
						// This is atlaskit's recommended pattern for custom trigger components
						// eslint-disable-next-line react/no-unstable-nested-components
						trigger={({ triggerRef, ...providedProps }: CustomTriggerProps) => (
							<DropdownMenuTrigger
								triggerRef={triggerRef}
								{...providedProps}
								pagination={pagination}
								textSize={textSize}
							/>
						)}
						shouldRenderToParent
						spacing="compact"
					>
						{PAGE_SIZE_OPTIONS.map((option) => (
							<DropdownItem
								key={option.value}
								onClick={() => {
									pagination.onSizeChange(option.value);
								}}
								isSelected={option.value === pagination.size}
							>
								<Text size={textSize}>{option.label}</Text>
							</DropdownItem>
						))}
					</DropdownMenu>
				</Box>
			</Inline>

			<Inline spread="space-between">
				<Inline alignBlock="center" alignInline="center" space="space.100">
					<Text size={textSize} color="color.text.subtle">
						<FormattedMessage
							{...messages.resultsCountText}
							values={{
								startIndex:
									pagination.totalElements === 0 ? 0 : pagination.page * pagination.size + 1,
								endIndex:
									pagination.totalElements === 0
										? 0
										: Math.min((pagination.page + 1) * pagination.size, pagination.totalElements),
								totalCount: pagination.totalElements,
							}}
						/>
					</Text>

					<Button
						onClick={pagination.onClickPrevious}
						spacing="compact"
						isDisabled={!pagination.hasPrevious}
					>
						<FormattedMessage {...messages.previousButton} />
					</Button>

					<Button
						onClick={pagination.onClickNext}
						spacing="compact"
						isDisabled={!pagination.hasNext}
					>
						<FormattedMessage {...messages.nextButton} />
					</Button>
				</Inline>
			</Inline>
		</Flex>
	);
};

const DropdownMenuTrigger = ({
	triggerRef,
	pagination,
	textSize,
	...providedProps
}: CustomTriggerProps & {
	pagination: FastshiftTablePaginationControls;
	textSize: 'small' | 'medium';
}) => (
	<Button
		ref={triggerRef as Ref<HTMLButtonElement>}
		{...providedProps}
		spacing="compact"
		onClick={(e) => {
			// sendClickDropdownEvent();
			providedProps.onClick?.(e);
		}}
	>
		<Inline space="space.100" alignInline="center" alignBlock="center">
			<Text size={textSize}>{pagination.size}</Text>
			<ChevronDownIcon label="" size="small" />
		</Inline>
	</Button>
);
